import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Upload, Loader2, Trash2, CheckCircle2, X, Power, PowerOff, ClipboardPaste, FileText, Save } from "lucide-react";

/**
 * /bid-skills — import a Perplexity/Cursor-style SKILL.md and BidForge will
 * apply it to every proposal it generates. Only one skill can be active per
 * workspace at a time.
 */
export default function BidSkills() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [tab, setTab] = useState("file"); // "file" | "paste"
  const [pasteName, setPasteName] = useState("");
  const [pasteContent, setPasteContent] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/bid-skills");
      setItems(r.data.items || []);
    } catch {
      /* no-op */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!/\.(md|markdown|txt)$/i.test(file.name)) {
      toast.error("Only .md, .markdown, and .txt files are supported");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("active", "true");
      const r = await api.post("/bid-skills/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded "${r.data.name}" and set as active`);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const savePaste = async () => {
    if (!pasteContent.trim()) {
      toast.error("Paste your skill markdown first");
      return;
    }
    setUploading(true);
    try {
      await api.post("/bid-skills", {
        name: pasteName || null,
        content: pasteContent,
        active: true,
      });
      toast.success("Skill saved and activated");
      setPasteName("");
      setPasteContent("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setUploading(false);
    }
  };

  const activate = async (id) => {
    try {
      await api.post(`/bid-skills/${id}/activate`);
      toast.success("Skill activated — next proposal will use it");
      await load();
    } catch {
      toast.error("Could not activate");
    }
  };

  const deactivateAll = async () => {
    try {
      await api.post("/bid-skills/deactivate");
      toast.success("Deactivated — proposals will use the default voice");
      await load();
    } catch {
      toast.error("Could not deactivate");
    }
  };

  const remove = async (skill) => {
    if (!window.confirm(`Delete "${skill.name}"?`)) return;
    try {
      await api.delete(`/bid-skills/${skill.skill_id}`);
      setItems((its) => its.filter((s) => s.skill_id !== skill.skill_id));
      toast.success("Removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  const anyActive = items.some((s) => s.active);

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <div className="flex items-center gap-4 mb-2">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/30">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="text-[10px] font-mono font-bold tracking-widest text-fuchsia-600">// HOUSE STYLE SKILLS</div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900">Teach BidForge your voice</h1>
        </div>
      </div>
      <p className="text-slate-600 max-w-3xl mb-8">
        Upload a Perplexity/Cursor-style SKILL.md file (or paste raw markdown) and BidForge will honor every rule
        inside — content-depth standard, section order, signature handling, compliance-checklist requirements — on
        every proposal it drafts. Only one skill is active at a time.
      </p>

      {/* Import tabs */}
      <div className="flex gap-1 mb-4">
        <button
          onClick={() => setTab("file")}
          className={`px-4 py-2 text-xs font-mono font-bold tracking-widest rounded-t-lg border-b-2 ${
            tab === "file"
              ? "border-fuchsia-500 text-fuchsia-700 bg-white"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
          data-testid="skill-tab-file"
        >
          <Upload className="w-3.5 h-3.5 inline mr-1.5" />
          UPLOAD SKILL.MD
        </button>
        <button
          onClick={() => setTab("paste")}
          className={`px-4 py-2 text-xs font-mono font-bold tracking-widest rounded-t-lg border-b-2 ${
            tab === "paste"
              ? "border-fuchsia-500 text-fuchsia-700 bg-white"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
          data-testid="skill-tab-paste"
        >
          <ClipboardPaste className="w-3.5 h-3.5 inline mr-1.5" />
          PASTE MARKDOWN
        </button>
      </div>

      {tab === "file" ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer?.files?.[0]); }}
          className={`rounded-2xl border-2 border-dashed p-8 mb-8 text-center transition-all ${
            dragging ? "border-fuchsia-500 bg-fuchsia-50" : "border-slate-300 bg-white hover:border-fuchsia-400"
          }`}
          data-testid="skill-dropzone"
        >
          <Upload className={`w-10 h-10 mx-auto mb-3 ${dragging ? "text-fuchsia-600" : "text-slate-400"}`} />
          <div className="font-display font-bold text-lg text-slate-900 mb-1">
            {uploading ? "Uploading…" : "Drop your SKILL.md here"}
          </div>
          <div className="text-sm text-slate-500 mb-4">.md, .markdown, or .txt · up to 500 KB · YAML frontmatter auto-parsed</div>
          <input
            ref={fileRef}
            type="file"
            accept=".md,.markdown,.txt"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="skill-file-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="skill-choose-file"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? "UPLOADING" : "CHOOSE FILE"}
          </button>
        </div>
      ) : (
        <div className="mb-8 space-y-3">
          <input
            value={pasteName}
            onChange={(e) => setPasteName(e.target.value)}
            placeholder="Skill name (optional — will use YAML frontmatter if present)"
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-fuchsia-400 outline-none"
            data-testid="skill-paste-name"
          />
          <textarea
            value={pasteContent}
            onChange={(e) => setPasteContent(e.target.value)}
            rows={12}
            placeholder={
              "---\nname: federal-bid-proposal-builder\ndescription: My house style for federal construction bids...\n---\n\n# Federal Bid Proposal Builder\n\n## Core Standard: Depth Over Brevity\n..."
            }
            className="w-full font-mono text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 focus:border-fuchsia-400 outline-none"
            data-testid="skill-paste-textarea"
          />
          <div className="flex items-center justify-end">
            <button
              onClick={savePaste}
              disabled={uploading || !pasteContent.trim()}
              className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="skill-paste-save"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {uploading ? "SAVING" : "SAVE & ACTIVATE"}
            </button>
          </div>
        </div>
      )}

      {/* Active-state banner */}
      <div
        className={`rounded-xl border p-4 mb-6 flex items-start gap-3 ${
          anyActive ? "border-fuchsia-300 bg-fuchsia-50" : "border-slate-200 bg-slate-50"
        }`}
        data-testid="skill-status-banner"
      >
        {anyActive ? (
          <CheckCircle2 className="w-5 h-5 text-fuchsia-600 mt-0.5 shrink-0" />
        ) : (
          <PowerOff className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
        )}
        <div className="flex-1">
          <div className={`font-display font-bold ${anyActive ? "text-fuchsia-900" : "text-slate-700"}`}>
            {anyActive
              ? `Active: ${items.find((s) => s.active)?.name}`
              : "No skill active — proposals use BidForge's default voice"}
          </div>
          <div className={`text-xs mt-0.5 ${anyActive ? "text-fuchsia-700" : "text-slate-500"}`}>
            {anyActive
              ? "Every new proposal, RFP draft, and bid-analysis will apply this skill's rules."
              : "Upload or paste a skill above to teach BidForge your voice."}
          </div>
        </div>
        {anyActive && (
          <button
            onClick={deactivateAll}
            className="text-xs font-mono font-bold tracking-widest text-fuchsia-700 hover:text-fuchsia-900 border border-fuchsia-300 hover:border-fuchsia-500 bg-white rounded-full px-3 py-1.5 inline-flex items-center gap-1.5"
            data-testid="skill-deactivate-all"
          >
            <PowerOff className="w-3.5 h-3.5" />
            TURN OFF
          </button>
        )}
      </div>

      {/* List */}
      <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="skill-empty">
            <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            No skills yet. Upload your first SKILL.md above.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="skill-list">
            {items.map((s) => {
              const expanded = expandedId === s.skill_id;
              return (
                <li key={s.skill_id} className="p-5" data-testid={`skill-row-${s.skill_id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display font-bold text-lg text-slate-900">{s.name}</div>
                        {s.active ? (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-mono font-bold tracking-widest bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full"
                            data-testid={`skill-active-badge-${s.skill_id}`}
                          >
                            <Power className="w-3 h-3" />
                            ACTIVE
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold tracking-widest bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                            INACTIVE
                          </span>
                        )}
                      </div>
                      {s.description && (
                        <p className="text-sm text-slate-600 mt-1 line-clamp-2">{s.description}</p>
                      )}
                      <div className="text-[10px] font-mono text-slate-400 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                        <span>SOURCE: {s.source || "paste"}</span>
                        <span>UPDATED: {new Date(s.updated_at).toLocaleDateString()}</span>
                        <span>SIZE: {s.content?.length?.toLocaleString?.() || 0} chars</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {!s.active && (
                        <button
                          onClick={() => activate(s.skill_id)}
                          className="text-xs font-mono font-bold tracking-widest bg-fuchsia-500 text-white hover:bg-fuchsia-600 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5"
                          data-testid={`skill-activate-${s.skill_id}`}
                        >
                          <Power className="w-3 h-3" />
                          ACTIVATE
                        </button>
                      )}
                      <button
                        onClick={() => setExpandedId(expanded ? null : s.skill_id)}
                        className="text-slate-400 hover:text-fuchsia-600 p-1.5"
                        title="Preview content"
                        data-testid={`skill-preview-${s.skill_id}`}
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(s)}
                        className="text-slate-300 hover:text-rose-500 p-1.5"
                        title="Delete"
                        data-testid={`skill-delete-${s.skill_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {expanded && (
                    <pre className="mt-4 max-h-96 overflow-auto bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs font-mono text-slate-700 whitespace-pre-wrap break-words">
                      {s.content}
                    </pre>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
